/* eslint-disable no-unsafe-optional-chaining */
import {Product} from '@amplify/athena-models';
import states from 'us-state-converter';
import type {
    ClientQuoteData,
    ClientWalkthroughData,
    ClientWalkthroughStateResponse,
    Phase3Type,
} from '../../../APIs/CRM';
import {
    Customer,
    Identification,
    Paperwork,
    PaperworkFormSpecType,
    PaperworkStatusType,
} from '../../../APIs/CustomerPortalAPI';
import {PaymentInformation} from '../../../Agent/types';
import Utils from '../../Utils';
import {DiabetesTreatmentForHubspot, SleepApneaLevel} from './conditionTypes';
import {ProductTypeForLeadTable} from './leadTypes';
import {IncomeRanges, State} from './other';

interface AllProductType extends Product {
    recommended?: boolean;
}
const CITIZENSHIP_ID_TYPES = new Set([
    'us_citizen',
    'permanent_resident_number',
    'us_visa',
    'taxpayer_identification_number',
]);

function computed<T = unknown>(compute: () => T) {
    return compute();
}

function medNumToParam(num: number | undefined): string | null {
    let param = null;
    if (num) {
        if (num < 4) param = num.toString();
        else param = 'Morethan3';
    }

    return param;
}

function freqCodeToParam(code: string | undefined): string | null {
    switch (code) {
        case '1ml':
            return 'Onceamonthorless';
        case '1wl':
            return 'Onceaweek';
        case '23w':
            return '_23timesaweek';
        case '4pw':
            return '_4timesaweek';
        default:
            return null;
    }
}

function productToParam(product: string | undefined): string | null {
    switch (product) {
        case 'Cigarettes':
            return 'Cigarettes';
        case 'E-cigarettes':
            return 'Ecigarettes';
        case 'Cigars':
            return 'Cigars';
        case 'Chewing Tobacco':
            return 'ChewingTobacco';
        case 'Nicotine Patch':
            return 'NicotinePatch';
        case 'Nicotine Gum':
            return 'NicotineGum';
        case 'Snuff':
            return 'Snuff';
        case 'Others':
            return 'Others';
        case 'Smoke':
            return 'Smoke';
        case 'Vape':
            return 'Vape';
        case 'Edible':
            return 'Edible';
        case 'Topical':
            return 'Topical';
        case 'Sniff':
            return 'Sniff';
        default:
            return null;
    }
}

function monthYearCodeToDate(str: string | undefined): Date | null {
    if (str) {
        const parts = str.split('/');
        return new Date(Number(parts[1]), Number(parts[0]) - 1);
    }

    return null;
}

function removeFormattingCharacters(str: string): string {
    return str ? str.replace(/[\W_]+/g, ' ') : str;
}

function capitalizeFirstLetter(str: string) {
    const words = str.split(' ');
    for (let i = 0; i < words.length; i += 1) {
        words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
    return words.join(' ');
}

function getPolicyType(policyType?: string) {
    switch (policyType) {
        case 'Accidental':
            return 'Accidental';
        case 'Guarantee Issue':
            return 'Guaranteedissue';
        case 'Guaranteed Issue':
            return 'Guaranteedissue';
        case 'Guarantee Universal Life':
            return 'Guaranteeduniversallife';
        case 'Indexed Universal Life':
            return 'Indexeduniversallife';
        case 'Variable Universal Life':
            return 'Variableuniversallife';
        case 'Term':
            return 'Term';
        case 'Whole Life':
            return 'Wholelife';
        case 'Unknown':
            return 'Dontknow';
        default:
            return null;
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const productTypeMap: any = {
    IUL: 'IUL',
    10: '_10yearTerm',
    15: '_15yearTerm',
    20: '_20yearTerm',
    25: '_25yearTerm',
    30: '_30yearTerm',
    35: '_35yearTerm',
    40: '_40yearTerm',
    GUL: 'GUL',
    Accidental: 'Accidental',
    VUL: 'VUL',
    'Guaranteed Issue': 'GuaranteedIssue',
};

function getFamilyCondition(diagnosedDiseases?: string[]) {
    return diagnosedDiseases
        ? diagnosedDiseases.map((diag) => {
              switch (diag) {
                  case 'none':
                      return 'NoneoftheAbove';
                  default:
                      return diag;
              }
          })
        : '';
}

function getRecommendedQuote(recommendedProductType?: string, phase3?: Phase3Type) {
    switch (recommendedProductType) {
        case 'vul':
            return {
                type: recommendedProductType,
                coverageAmount: 100000,
                cashValue: phase3?.vul.vul?.toFixed(2),
                monthlyPremium: phase3?.vul.contribution,
            };
        case 'iul':
            return {
                type: recommendedProductType,
                coverageAmount: phase3?.resultRetirement.lifetimeCoverage,
                cashValue: (phase3?.resultRetirement.taxFreeCash || 0).toFixed(2),
                monthlyPremium: phase3?.resultRetirement.perMonth,
                yearCoverage: phase3?.resultRetirement.years,
            };
        case 'term':
            return {
                type: recommendedProductType,
                coverageAmount: phase3?.resultTemporary.lifetimeCoverage,
                monthlyPremium: phase3?.resultTemporary.perMonth,
                yearCoverage: phase3?.resultTemporary.yearCoverage,
            };
        case 'combo':
            return {
                type: recommendedProductType,
                coverageAmount:
                    (phase3?.resultBest.year20_LifeCoverage || 0) + (phase3?.resultBest.year1_20Coverage || 0),
                cashValue: phase3?.resultBest.taxFreeCash,
                monthlyPremium: phase3?.resultBest.perMonth,
            };
        case 'Basic Permanent':
            return {
                type: recommendedProductType,
                coverageAmount: phase3?.resultBasic.lifetimeCoverage,
                monthlyPremium: phase3?.resultBasic.perMonth,
            };
        default:
            return {};
    }
}

function getConvertedWalkthrough(data: ClientWalkthroughData, walkthroughState?: ClientWalkthroughStateResponse['v']) {
    return {
        best: data?.best?.quote,
        retirement: data?.retirement?.quote,
        temporary: data?.temporary?.quote,
        basic: data?.basic?.quote,
        vul: data?.approval,
        recommended: computed(() =>
            getRecommendedQuote(walkthroughState?.calcStore.activeProduct, walkthroughState?.userData?.phase3),
        ),
        hasSelectedProduct: walkthroughState?.calcStore.hasSelectedProduct,
        investmentGoals: walkthroughState?.userData.phase1.features,

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        approvalQuote: ProductTypeForLeadTable[data?.approval?.product_type],
        preferredCallDate: data?.call_date ? new Date(data?.call_date) : undefined,
        preferredCallTime: data?.call_time_slot,
    };
}

function handleCommaSeparatedNumber(value: any) {
    if (value) {
        if (typeof value === 'string') {
            return value.replaceAll(',', '');
        }
        return value;
    }
    return '';
}

// pre-fill the form with data from different sources
// in order of importance, customer portal data, walkthrough, hubspot
export const convertWalkthroughData = (
    data: ClientWalkthroughData | null,
    walkthroughState?: ClientWalkthroughStateResponse['v'],
    customerPortalData?: Customer,
    customerQuoteData?: ClientQuoteData[],
) => {
    // Handle null walkthrough data gracefully
    if (!data) {
        return {
            firstName: customerPortalData?.firstName || '',
            lastName: customerPortalData?.lastName || '',
            email: customerPortalData?.email || '',
            emailAddress: customerPortalData?.email || '',
            walkthroughData: {
                best: null,
                retirement: null,
                temporary: null,
                basic: null,
                vul: null,
                recommended: {},
                hasSelectedProduct: false,
                investmentGoals: [],
                approvalQuote: undefined,
                preferredCallDate: undefined,
                preferredCallTime: undefined,
            },
        };
    }

    if (!data?.condition) {
        // eslint-disable-next-line no-console
        console.error('YOU MIGHT NOT BE USING THE CORRECT ATHENA URL, PLEASE DOUBLE CHECK YOUR ATHENA URL');
        console.log('YOU MIGHT NOT BE USING THE CORRECT ATHENA URL, PLEASE DOUBLE CHECK YOUR ATHENA URL');
    }

    const {condition} = data;

    const state = walkthroughState?.userData;
    // For time, we are receiving some time from Hubspot which they randomly input some time off set based on the user's location
    // The format they will return is YYYY-MM-DDTHH:mm:ss.zzzZ
    // so, instead of using Date() function, which will add another time off set based on the user's location,
    // just parse the string straight and pass them
    const returnData: any = {
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        emailAddress: data.email,
        weight: data.weight,
        phoneNumber: data.phone_number,
        monthlyBudget: data.monthly_budget,
        height: Utils.Height2Inch('' + data.feet + "'" + data.inch + '"'),
        bday: data.date_of_birth ? data.date_of_birth.substring(8, 10) : '',
        bmonth: data.date_of_birth ? data.date_of_birth.substring(5, 7) : '',
        byear: data.date_of_birth ? data.date_of_birth.substring(0, 4) : '',
        gender: data.gender === 1 ? 'Male' : 'Female',
        healthConditions: !!Object.keys(data.condition ?? {}).some((v) => {
            if (v !== 'none_conditions') return data.condition[v];
            return !data.condition[v];
        }),
        // Bloodpressure: !!condition?.high_blood_pressure?.medications,

        bpMedications: +condition?.high_blood_pressure?.medications
            ? +condition?.high_blood_pressure?.medications
            : undefined,
        Asthma: !!condition?.astma && JSON.stringify(condition.astma) !== '{}',
        asthmaHospitalizations: !!condition?.astma?.hospitalize,
        // Cancer: !!condition?.cancer,
        // BasalCellCarcinoma: !!condition?.cancer?.basal_cell,
        // cholesterol: !!condition?.cholesterol,
        Cholesterol: computed(() => state?.phase2?.diagCond?.find((cond) => cond.label === 'High cholesterol')?.value),
        cholesterolReading: state?.phase2?.diagHighCholHDL,
        cholesterolReadingAlt: state?.phase2?.diagHighCholHDL,
        Anxiety: !!state?.phase2?.diagPsychCondition?.includes('anxiety'),
        BipolarDisorder: !!state?.phase2?.diagPsychCondition?.includes('bipolar'),
        Schizophrenia: !!state?.phase2?.diagPsychCondition?.includes('schizophrenia'),
        Depression: !!state?.phase2?.diagPsychCondition?.includes('depression'),
        Type1Diabetes: condition?.diabetes?.type === 'type1',
        Type2Diabetes: condition?.diabetes?.type === 'type2',

        type1A1CReading: condition?.diabetes?.type === 'type1' ? state?.phase2?.diagDiabetesA1C : undefined,
        type2A1CReading: condition?.diabetes?.type === 'type2' ? state?.phase2?.diagDiabetesA1C : undefined,

        type1Treatment:
            condition?.diabetes?.type === 'type1'
                ? // @ts-ignore
                  DiabetesTreatmentForHubspot[condition?.diabetes?.treatment]
                : undefined,

        type2Treatment:
            condition?.diabetes?.type === 'type2'
                ? // @ts-ignore
                  DiabetesTreatmentForHubspot[condition?.diabetes?.treatment]
                : undefined,

        SleepApnea: computed(() => state?.phase2?.diagCond?.find((cond) => cond.label === 'Sleep apnea')?.value),
        // @ts-ignore
        sleepapneaSeverity: SleepApneaLevel[state?.phase2?.diagSleepLevel],
        sleepapneaTreatment: state?.phase2?.diagSleepCPAP,

        HeartFailure: !!condition?.heart_attack,
        heartAttackLastDate: condition?.heart_attack?.last_occur
            ? new Date(condition?.heart_attack?.last_occur)
            : undefined,

        Stroke: !!condition?.stroke,
        strokeDiagnosisDate: condition?.stroke?.last_occur ? new Date(condition?.stroke?.last_occur) : undefined,
        strokeLastOccurence: condition?.stroke?.last_occur
            ? new Date(condition?.stroke?.last_occur).getFullYear()
            : undefined,

        duiDate:
            new Date().getFullYear() - new Date(data?.driving?.dui?.occur).getFullYear() >= 5
                ? 'Morethanfiveyearsago'
                : 'Lessthanfiveyearsago',

        otherHealthConditions: condition?.other_serious_condition?.description,

        // @ts-ignore
        state: states.abbr(Object.keys(State).filter((k) => !+k)[data?.state - 1]),
        // @ts-ignore
        annualIncomeFinancial: IncomeRanges[(Math.abs(data?.household_income) || 1) - 1],

        incomeHouseholdText: handleCommaSeparatedNumber(state?.phase1.annualHouseholdIncomeText),
        estimatedDebtTotal: handleCommaSeparatedNumber(state?.phase1.estimatedDebtTotal),
        incomeIndividual: handleCommaSeparatedNumber(state?.phase1.annualIndividualIncome),

        budget: +data?.monthly_budget,
        // cmCall: data.call_date ? new Date(data.call_date) : undefined,
        premiumAmountVUL: +data.approval?.contribution || 0,
        // data.approval.user_selected_perm_premium,

        walkthroughData: computed(() => getConvertedWalkthrough(data, walkthroughState)),

        // Intent builder fields
        intentFocus: computed(() => {
            const focus = state?.phase1?.wealthGoals?.[0] ?? state?.phase1?.protectionGoals?.[0];
            if (focus === 'longTermSavings') {
                return 'SAVINGS';
            } else if (focus === 'nearTermRetirement') {
                return 'RETIREMENT';
            } else if (['legacy', 'beneficiaries'].includes(focus ?? '')) {
                return 'PROTECTION';
            } else if (['temporaryLiabilities', 'criticalIllness', 'other'].includes(focus ?? '')) {
                return 'INHERITANCE';
            }
            return undefined;
        }),
        intentFunding: computed(() => {
            const funding = state?.phase1?.growFundingPlan?.[0];
            if (['lumpSumSavings', 'lumpSumRetirement'].includes(funding ?? '')) {
                return 'FUND_LUMP';
            } else if (funding === 'monthlyContributions') {
                return 'FUND_MONTHLY';
            } else if (funding === 'both') {
                return 'FUND_BOTH';
            }
            return undefined;
        }),

        // Merge in state not found in platform-walkthrough-data
        addressZipCode: state?.phase1?.zipCode,
        usCitizenship: computed(() => {
            if (typeof state?.phase1?.usCitizen === 'boolean') {
                return state?.phase1?.usCitizen ? 'Yes' : null;
            }
            return undefined;
        }),
        birthCountry: state?.phase1?.birthCountry ? capitalizeFirstLetter(state.phase1.birthCountry) : '',
        birthState: computed(() => {
            if (state?.phase1?.birthState === 'Not Born in the U.S.') {
                return 'NotbornintheUS';
            }
            return state?.phase1?.birthState;
        }),
        address: state?.phase1?.address?.Street,
        addressCity: state?.phase1?.address?.City,
        addressZip: state?.phase1?.zipCode,
        currentlyEmployed: computed(() => {
            if (typeof state?.phase1?.employmentStatus === 'boolean') {
                return state?.phase1?.employmentStatus ? 'Employed' : null;
            } else if (typeof state?.phase1?.employmentStatus === 'string') {
                switch (state?.phase1?.employmentStatus) {
                    case 'employed':
                        return 'Employed';
                    case 'unemployed':
                        switch (state?.phase1?.unemploymentReasonType) {
                            case 'Unemployed':
                                return 'Unemployed';
                            case 'Retired':
                                return 'Retired';
                            case 'Student':
                                return 'Student';
                            case 'Homemaker':
                                return 'HomemakerSpouseEmployed';
                            case 'Other':
                                return 'Other';
                            case 'On Disability':
                                return 'Disability';
                            default:
                                return 'Unemployed';
                        }
                    default:
                        return null;
                }
            }
            return undefined;
        }),
        estimatedSavingsTotal: state?.phase1?.estimatedSavingTotal,
        reasonUnemployed: computed(() => {
            return state?.phase1?.unemploymentReasonDesc;
        }),
        previousJobInfo: computed(() => {
            switch (state?.phase1?.unemploymentReasonType) {
                case 'Unemployed':
                    return (
                        state?.phase1?.prevEmployer +
                        ' - ' +
                        state?.phase1?.workExpirienceYears +
                        ' years - ' +
                        state?.phase1?.prevOccupation
                    );
                case 'Student':
                    return 'Student';
                case 'Homemaker':
                    return 'HomemakerSpouseEmployed';
                case 'Other':
                    return 'Other';
                case 'On Disability':
                    return 'OnDisability';
                default:
                    return null;
            }
        }),
        retiredInfo: computed(() => {
            if (state?.phase1?.unemploymentReasonType == 'Retired') {
                return state?.phase1?.ageOfRetirement;
            } else {
                return null;
            }
        }),
        retiredIncome: computed(() => {
            if (state?.phase1?.unemploymentReasonType == 'Retired') {
                return state?.phase1?.currIncomeDetails;
            } else {
                return null;
            }
        }),
        schoolInfo: computed(() => {
            if (state?.phase1?.unemploymentReasonType == 'Student') {
                return state?.phase1?.schoolName + ' - ' + state?.phase1?.grade;
            } else {
                return null;
            }
        }),
        disabilityInfo: computed(() => {
            if (state?.phase1?.unemploymentReasonType == 'On Disability') {
                return state?.phase1.unemploymentReasonDesc + ' - ' + state?.phase1?.termSSDI;
            } else {
                return null;
            }
        }),
        otherInfo: computed(() => {
            if (state?.phase1?.unemploymentReasonType == 'Other') {
                return state?.phase1.unemploymentReasonDesc;
            } else {
                return null;
            }
        }),
        maritalStatus: state?.phase1?.maritalStatus,
        numberOfChildren: state?.phase1?.numberOfChildren,
        military: state?.phase1?.military,
        isCurrentlyMilitary: state?.phase1?.military,
        travelingOut: state?.phase1?.travellingOut,
        extremeSports: computed(() => {
            const extremeSports = state?.phase1?.extremeSports;
            if (extremeSports) {
                return Array.isArray(extremeSports) && !extremeSports.includes('none');
            }
            return undefined;
        }),
        extremeSportsOptions: computed(() => {
            const extremeSports = state?.phase1?.extremeSports;
            if (extremeSports && Array.isArray(extremeSports) && !extremeSports.includes('none')) {
                return extremeSports.map((item) => {
                    const sportName = item.split(/(?=[A-Z])/); // ['scuba','Diving']
                    sportName[0] = sportName[0].substring(0, 1).toUpperCase() + sportName[0].substring(1);
                    return sportName.join('') === 'Racing' ? 'CarRacing' : sportName.join('');
                });
            }
            return undefined;
        }),
        existingCoverage: state?.phase1?.existingCoverage,
        existingPolicyType: computed(() => getPolicyType(state?.phase1?.policyType)),
        existingCoverageAmount: computed(() => {
            let coverageAmount = state?.phase1?.coverageAmount;
            if (coverageAmount && typeof coverageAmount === 'string') {
                coverageAmount = Number(coverageAmount.replace(/[^0-9.]+/g, ''));
            }
            return coverageAmount;
        }),
        shoppedCoverage: state?.phase1.existingCoverageApplied === 'true',
        shoppedPolicyType: computed(() => getPolicyType(state?.phase1.policyTypeApplied)),
        shoppedCoverageAmount: state?.phase1.coverageAmountApplied,

        tobacco: state?.phase2?.usedTobacco || state?.phase2?.usedMarijuana,
        tobaccoUsage: computed(() =>
            Utils.MakeVarName(
                state?.phase2.usedMarijuanaProduct ? 'Marijuana' : (state?.phase2.usedTobaccoProduct ?? ''),
            ),
        ),
        tobaccoUsageDate: state?.phase2?.usedMarijuana
            ? state?.phase2?.usedMarijuanaLastDate
            : state?.phase2?.usedTobaccoLastDate,
        tobaccoMethod: state?.phase2?.usedMarijuanaProduct,

        biologicalFamilyCondition: computed(() => getFamilyCondition(state?.phase2?.healthFamilyMedical)),
        HIV: state?.phase2?.aids,
        HIVAIDS: state?.phase2?.aids, // Sync both HIV fields
        // Behavior data
        DrugorAlcoholDependenceWithdrawal: computed(
            () => state?.phase2?.behaviors?.find((cond) => cond.label === 'Alcohol and/or Drug Dependence')?.value,
        ),
        weeklyAlcoholicDrinks: state?.phase2?.alcoholicDrinksWeekly,
        alcoholDrinkType: state?.phase2?.alcoType,
        alcoholBeverages: state?.phase2?.alcoBeverages,
        alcoholTreatment: state?.phase2?.treatment,
        alcoholTreatmentClinic: state?.phase2?.treatmentClinic,
        alcoholDrugFree: computed(() => state?.phase2?.alcoDrugFreeYears?.toString().trim()),
        alcoholTreatmentDetails: state?.phase2?.treatmentDetails,
        alcoholTreatmentDate: computed(() => monthYearCodeToDate(state?.phase2?.treatmentWhen)),

        DUI: computed(() => state?.phase2?.behaviors?.find((cond) => cond.label === 'DUI')?.value),
        DUIDate: computed(() => monthYearCodeToDate(state?.phase2?.DUIWhen)),

        Movingviolations: computed(
            () => state?.phase2?.behaviors?.find((cond) => cond.label === 'Moving Violations')?.value,
        ),
        violationNumbers: state?.phase2?.movingViolationsHowMany,

        License: computed(() => state?.phase2?.behaviors?.find((cond) => cond.label === 'Suspended License')?.value),
        suspendedLicense: state?.phase2?.suspendedLicenseStill,
        suspendedLicenseDate: computed(() => monthYearCodeToDate(state?.phase2?.suspendedLicenseWhen)),

        Criminaloffense: computed(
            () => state?.phase2?.behaviors?.find((cond) => cond.label === 'Criminal Offense')?.value,
        ),
        criminalOffenseDetails: state?.phase2?.criminalOffenseDetails,
        criminalOffenseDate: computed(() => monthYearCodeToDate(state?.phase2?.criminalOffenseWhen)),
        criminalParole: computed(() => {
            switch (state?.phase2?.paroleComplete) {
                case 'lessthan5':
                    return 'Inthelastfiveyears';
                case '5to10':
                    return 'Inthelastfivetotenyears';
                case 'morethan10':
                    return 'Overtenyearsago';
                default:
                    return null;
            }
        }),
        Bankruptcy: computed(() => state?.phase2?.behaviors?.find((cond) => cond.label === 'Bankruptcy')?.value),
        bankruptcyYear: state?.phase2?.bankruptcyYear,
        bankruptcyChapter: computed(() => (state?.phase2?.bankruptcyChapter ? state?.phase2?.bankruptcyChapter : null)),
        bankruptcyOpen: state?.phase2?.bankruptcyIsOpen,
        bankruptcyDischargeDate: computed(() => monthYearCodeToDate(state?.phase2?.bankruptcyDischargedWhen)),

        Bloodpressure: computed(
            () => state?.phase2?.diagCond?.find((cond) => cond.label === 'High blood pressure')?.value,
        ),
        bpDiagnosisDate: computed(() => monthYearCodeToDate(state?.phase2?.diagBloodPressDiagnosisDate)),
        bpReadingLow: state?.phase2?.diagBloodPressLow,
        bpReadingHigh: state?.phase2?.diagBloodPressHigh,
        bpNumMedications: state?.phase2?.diagBloodPressMedication,

        Cancer: computed(() => state?.phase2?.diagCond?.find((cond) => cond.label === 'Cancer')?.value),
        BasalCellCarcinoma: computed(
            () =>
                state?.phase2?.diagCond?.find((cond) => cond.label === 'Cancer')?.value &&
                !!state?.phase2?.diagBasalCellCancer,
        ),
        basalcellDiagnosisDate: computed(() => monthYearCodeToDate(state?.phase2?.diagBasalCellCancer)),

        TobaccoRating: computed(
            () => state?.phase2?.usedTobacco || state?.phase2?.diagProductsUsed?.includes('Cigarettes'),
        ),
        tobaccoProducts: computed(() => productToParam(state?.phase2?.usedTobaccoProduct)),
        tobaccoFrequency: computed(() => freqCodeToParam(state?.phase2?.usedTobaccoFrequency)),
        tobaccoLastUse: computed(() => monthYearCodeToDate(state?.phase2?.usedTobaccoLastDate)),

        MarijuanaRating: computed(
            () => state?.phase2?.usedMarijuana || state?.phase2?.diagProductsUsed?.includes('Marijuana'),
        ),
        marijuanaProducts: computed(() => productToParam(state?.phase2?.usedMarijuanaProduct)),
        marijuanaFrequency: computed(() => freqCodeToParam(state?.phase2?.usedMarijuanaFrequency)),
        marijuanaLastUse: computed(() => monthYearCodeToDate(state?.phase2?.usedMarijuanaLastDate)),

        OtherRating: computed(
            () => state?.phase2?.otherSmokeProductUsed || state?.phase2?.diagProductsUsed?.includes('Other'),
        ),
        otherSubstance: state?.phase2?.otherSmokeProductSubstance,
        otherProducts: computed(() => productToParam(state?.phase2?.otherSmokeProductProduct)),
        otherFrequency: computed(() => freqCodeToParam(state?.phase2?.otherSmokeProductFrequency)),
        otherLastUse: computed(() => monthYearCodeToDate(state?.phase2?.otherSmokeProductLastDate)),

        psychNumMedications: computed(() => medNumToParam(state?.phase2?.diagPsychMedication)),
        psychHospitalization: computed(() => state?.phase2?.diagPsychLastHosp),
    };
    // Treat KO answers that come back as default null as unanswered so AP does not assume a "No"
    // for customers who never actually responded in the walkthrough.
    const knockoutFields = [
        'DrugorAlcoholDependenceWithdrawal',
        'DUI',
        'License',
        'Movingviolations',
        'Criminaloffense',
        'Bankruptcy',
        'HIV',
        'HIVAIDS',
        'SleepApnea',
        'Cancer',
        'BipolarDisorder',
        'Schizophrenia',
        'Defibrillator',
        'Transplant',
    ];

    knockoutFields.forEach((field) => {
        const value = returnData[field];
        if (value === null || value === undefined || value === '' || value === 'null') {
            returnData[field] = undefined;
        }
    });
    if (typeof state?.phase2.primaryCare === 'boolean') {
        returnData.hasPrimaryCarePhysician = state?.phase2.primaryCare;
    }

    // expand this to fill additional data from the customer portal
    if (customerPortalData) {
        if (customerPortalData.firstName) {
            returnData.firstName = customerPortalData.firstName;
        }
        if (!!customerPortalData.lastName) {
            returnData.lastName = customerPortalData.lastName;
        }
        if (!!customerPortalData.dateOfBirth) {
            [returnData.byear, returnData.bmonth, returnData.bday] = customerPortalData.dateOfBirth.split('-');
        }
        if (!!customerPortalData?.identifications?.length) {
            const lastUpdatedIdentification = customerPortalData.identifications
                .filter((id) => CITIZENSHIP_ID_TYPES.has(id.type), 'updated_at')
                .sort((a, b) => {
                    const aDate = new Date(a.updated_at);
                    const bDate = new Date(b.updated_at);
                    return bDate.getTime() - aDate.getTime();
                })[0];

            let citizenshipFromCustomerPortal;
            switch (lastUpdatedIdentification?.type) {
                case 'us_citizen':
                    citizenshipFromCustomerPortal = 'Yes';
                    break;
                case 'permanent_resident_number':
                    citizenshipFromCustomerPortal = 'PermanentResident';
                    break;
                case 'us_visa':
                    citizenshipFromCustomerPortal = 'USVisaHolder';
                    break;
                case 'taxpayer_identification_number':
                    citizenshipFromCustomerPortal = 'NoUSVisaTINonly';
                    break;
                default:
                    citizenshipFromCustomerPortal = undefined;
                    break;
            }
            if (citizenshipFromCustomerPortal) returnData.usCitizenship = citizenshipFromCustomerPortal;

            // TODO : Move this object to a different file when you know all the identity fields
            // that we collect on customer portal
            // or intend to collect
            const mapper: any = {
                us_citizen: [{app: 'socialSecurityNumber', db: 'socialSecurityNumber'}],
                permanent_resident_number: [
                    {app: 'usPermResNumber', db: 'permanentResidentNumber'},
                    {app: 'socialSecurityNumber', db: 'socialSecurityNumber'},
                ],
                us_visa: [
                    {app: 'usVisaType', db: 'usVisaType'},
                    {app: 'usVisaNumber', db: 'usVisaNumber'},
                    {app: 'socialSecurityNumber', db: 'socialSecurityNumber'},
                ],
                taxpayer_identification_number: [{app: 'usTinNumber', db: 'usTinNumber'}],
                drivers_license: [
                    {app: 'driversLicExpiration', db: 'exp'},
                    {app: 'driversLicenseNumber', db: 'number'},
                    {app: 'driversLicenseState', db: 'state'},
                ],
                state_issued_id: [
                    {app: 'stateIdExpiration', db: 'exp'},
                    {app: 'stateIdNumber', db: 'number'},
                    {app: 'stateIdState', db: 'state'},
                    {app: 'noDriversLicenseReason', db: 'reasonNoLicense'},
                    {app: 'modeOfTransport', db: 'modeOfTransport'},
                ],
                no_id: [
                    {app: 'ndiReason', db: 'reason'},
                    {app: 'noDriversLicenseReason', db: 'reasonNoLicense'},
                    {app: 'modeOfTransport', db: 'modeOfTransport'},
                ],
                no_id_minor: [
                    {app: 'noDriversLicenseReason', db: 'reasonNoLicense'},
                    {app: 'modeOfTransport', db: 'modeOfTransport'},
                ],
            };
            customerPortalData.identifications.forEach((item: Identification) => {
                const value: any = JSON.parse(item.value);
                mapper[item.type]?.forEach((fieldMapperObject: any) => {
                    returnData[fieldMapperObject.app] = value[fieldMapperObject.db];
                    if (
                        fieldMapperObject.app === 'socialSecurityNumber' ||
                        fieldMapperObject.app === 'driversLicenseNumber'
                    )
                        if (typeof returnData[fieldMapperObject.app] === 'string') {
                            returnData[fieldMapperObject.app] = returnData[fieldMapperObject.app]?.replace(/-/g, '');
                        }
                    if (fieldMapperObject.app === 'noDriversLicenseReason' && value.reasonNoLicense) {
                        const reasonOption = value.reasonNoLicense as string;
                        // eslint-disable-next-line no-nested-ternary
                        returnData.noDriversLicenseReason = reasonOption.includes('obtained')
                            ? 'Ihaveneverhadalicense'
                            : reasonOption.includes('lapsed')
                              ? 'Mylicensehaslapsed'
                              : 'Mylicenseissuspended';
                    }
                });
                switch (item.type) {
                    case 'drivers_license':
                        returnData.validDriversLicense = 'Yes';
                        break;
                    case 'state_issued_id':
                        returnData.validDriversLicense = 'StateID';
                        break;
                    case 'no_id':
                        returnData.validDriversLicense = 'NoID'; // handle it here some thing like,
                        break;
                    case 'no_id_minor':
                        returnData.validDriversLicense = 'NoIDApplicantisaMinor';
                        break;
                }
            });
        }

        if (customerPortalData?.paymentInformations?.length) {
            const value: PaymentInformation =
                customerPortalData.paymentInformations[customerPortalData.paymentInformations.length - 1];
            if (!!value.routingNumber && !!value.accountNumber) {
                returnData.conditionalCoverage = true;
                returnData.conditionalRoutingNumber = value.routingNumber;
                returnData.conditionalAccountNumber = value.accountNumber;
                returnData.conditionalDraftDate = value.dayOfMonth;
                returnData.conditionalPaymentFreq = value.paymentFrequency
                    ? value.paymentFrequency.replace(/\s/g, '')
                    : 'Monthly';
            }
        }
        if (customerPortalData.requirements) {
            // keep requirements employment info for older data
            const employmentInfo = customerPortalData.requirements
                .filter((r) => r.type === 'employmentInfo')
                .sort((a, b) => {
                    const aDate = new Date(a.updatedAt);
                    const bDate = new Date(b.updatedAt);
                    return bDate.getTime() - aDate.getTime();
                })[0];
            if (employmentInfo && employmentInfo.data_json) {
                const employmentData = JSON.parse(employmentInfo.data_json);
                returnData.nameOfEmployer = employmentData.employerName;
                returnData.industryDuties = removeFormattingCharacters(employmentData.industryDescription);
                returnData.occupation = employmentData.occupation;
                if (employmentData.employmentStartDate) {
                    const employmentDate = employmentData.employmentStartDate.split('/');
                    returnData.currentlyEmployed = 'Employed';
                    if (employmentDate && employmentDate.length > 1) {
                        const yearOfEmployed = employmentDate[1];
                        returnData.timeEmployed = new Date().getFullYear() - yearOfEmployed;

                        const d = new Date();
                        d.setDate(1);
                        d.setMonth(parseInt(employmentDate[0], 10) - 1);
                        d.setFullYear(parseInt(employmentDate[1], 10));

                        returnData.employmentDays = Math.round((Date.now() - d.getTime()) / 86400000);
                    }
                }
                returnData.currentSelfEmployed = employmentData.isSelfEmployed;
                returnData.prevSelfEmployed = employmentData.prevIsSelfEmployed;
                returnData.prevEmployName = employmentData.prevEmployerName;
                returnData.prevEmployStart = employmentData.prevEmploymentStartDate;
                returnData.prevEmployOccup = employmentData.prevOccupation;
                returnData.prevEmployIndustry = removeFormattingCharacters(employmentData.prevIndustryDescription);
            }
            const primaryCareProviderInfo = customerPortalData.requirements.find(
                (r) => r.type === 'primaryCareProvider' && r.status === 'received',
            );
            if (primaryCareProviderInfo && primaryCareProviderInfo.data_json) {
                const careProviderData = JSON.parse(primaryCareProviderInfo.data_json);
                returnData.primaryCarePhysician = careProviderData.hospital_name;
                const {address} = careProviderData;
                if (address) {
                    returnData.primaryCarePhysicianAddress = `${address.Street} ${address.City} ${address.State} ${address.ZipCode}`;
                    returnData.primaryCarePhysicianStreet = address.Street;
                    returnData.primaryCarePhysicianCity = address.City;
                    returnData.primaryCarePhysicianState = address.State;
                    returnData.primaryCarePhysicianZipCode = address.ZipCode;
                }
                returnData.primaryCarePhysicianPhone = careProviderData.phone_number;
                returnData.lastVisit = careProviderData.last_visit_pcp;
                returnData.visitReason = careProviderData.visit_reason;
                returnData.visitOutcome = careProviderData.visit_outcome;
            }
        }

        if (customerPortalData.paperworks) {
            const getPaperworkData = (paperworkData: Paperwork) => JSON.parse(paperworkData.completed_paperwork);
            // Beneficiaries paperworks
            const confirmedPaperworkBeneficiaries = customerPortalData.paperworks.find(
                (r) =>
                    r.status === PaperworkStatusType.CONFIRMED &&
                    r.form_spec.label === PaperworkFormSpecType.BENEFICIARIES,
            );

            /* eslint-disable prefer-destructuring */
            if (confirmedPaperworkBeneficiaries) {
                const paperworkData = getPaperworkData(confirmedPaperworkBeneficiaries);
                let noOfbene = 0;
                let contigentBene = 0;
                Object.entries(paperworkData).forEach((key, index, value) => {
                    const beneficiaryData: any = value[index][1];
                    if (beneficiaryData) {
                        if (beneficiaryData.contingent) {
                            contigentBene += 1;
                            returnData[`contBeneInfo${contigentBene}`] =
                                `${beneficiaryData.firstName} ${beneficiaryData.lastName}`;
                            returnData[`contBenePercent${contigentBene}`] = beneficiaryData.payoutPercentage;
                            returnData[`contBeneRelationship${contigentBene}`] = capitalizeFirstLetter(
                                beneficiaryData.relationship,
                            );
                            if (beneficiaryData.relationshipNote)
                                returnData[`contBeneRelationshipNote${contigentBene}`] =
                                    beneficiaryData.relationshipNote;
                            const beneDOB = beneficiaryData['dateOfBirth'];
                            if (beneDOB) {
                                const splitDOB = beneDOB.split('/');
                                if (splitDOB.length === 3) {
                                    returnData[`contBeneMonth${contigentBene}`] = splitDOB[0];
                                    returnData[`contBday${contigentBene}`] = splitDOB[1];
                                    returnData[`contBeneYear${contigentBene}`] = splitDOB[2];
                                }
                            }
                        } else {
                            noOfbene += 1;
                            returnData[`beneInfo${noOfbene}`] =
                                `${beneficiaryData['firstName']} ${beneficiaryData['lastName']}`;
                            returnData[`benePercent${noOfbene}`] = beneficiaryData.payoutPercentage;

                            returnData[`beneRelationship${noOfbene}`] = capitalizeFirstLetter(
                                beneficiaryData.relationship,
                            );
                            if (beneficiaryData.relationshipNote)
                                returnData[`beneRelationshipNote${noOfbene}`] = beneficiaryData.relationshipNote;
                            const beneDOB = beneficiaryData['dateOfBirth'];
                            if (beneDOB) {
                                const splitDOB = beneDOB.split('/');
                                if (splitDOB.length === 3) {
                                    returnData[`beneMonth${noOfbene}`] = splitDOB[0];
                                    returnData[`beneDay${noOfbene}`] = splitDOB[1];
                                    returnData[`beneYear${noOfbene}`] = splitDOB[2];
                                }
                            }
                        }
                    }
                });
                /* eslint-enable prefer-destructuring */
                if (noOfbene) {
                    returnData.numberOfBenes = noOfbene;
                }
                if (contigentBene) {
                    returnData.contBene = true;
                    returnData.contNumberOfBenes = contigentBene;
                }
            }

            const additionalConditionsPaperwork = customerPortalData.paperworks.find(
                (p) => p.form_spec.label === PaperworkFormSpecType.ADDITIONAL_CONDITIONS,
            );

            if (additionalConditionsPaperwork) {
                returnData.additionalConditions = getPaperworkData(additionalConditionsPaperwork);
            }

            const additionalPolicyInterestsPaperwork = customerPortalData.paperworks.find(
                (p) => p.form_spec.label === PaperworkFormSpecType.ADDITIONAL_POLICY_INTEREST,
            );

            if (additionalPolicyInterestsPaperwork) {
                const paperworkData = getPaperworkData(additionalPolicyInterestsPaperwork);
                Object.entries(paperworkData).forEach(([, value]) => {
                    const interests = String(value);

                    if (interests === '') {
                        returnData.interestedInAdditionalPolicy = 'No Answer';
                    } else {
                        returnData.interestedInAdditionalPolicy = interests;
                    }
                });
                returnData.upsellMaritalStatus = state?.phase1.maritalStatus;
                returnData.kidsNumber = state?.phase1.numberOfChildren;
            }

            /**
             * Customer portal's address-paperwork to pre-approval's basic-information
             */
            const confirmedAddress = customerPortalData.paperworks.find(
                (r) =>
                    r.status === PaperworkStatusType.CONFIRMED && r.form_spec.label === PaperworkFormSpecType.ADDRESS,
            );

            if (confirmedAddress) {
                try {
                    const address = JSON.parse(confirmedAddress.completed_paperwork);
                    returnData.address = address.Street;
                    returnData.addressCity = address.City;
                    returnData.state = address.State;
                    returnData.addressZipCode = address.ZipCode;
                } catch (error) {
                    console.error('Failed to parse paperwork address');
                }
            }

            // Additional health paperworks
            const confirmedPaperworkAdditionalHealth = customerPortalData.paperworks.find(
                (r) =>
                    r.status === PaperworkStatusType.CONFIRMED &&
                    r.form_spec.label === PaperworkFormSpecType.ADDITIONAL_HEALTH,
            );
            /* eslint-disable prefer-destructuring */
            if (confirmedPaperworkAdditionalHealth) {
                const paperworkData = JSON.parse(confirmedPaperworkAdditionalHealth.completed_paperwork);

                // PCP Fields
                returnData.primaryCarePhysician = paperworkData.pcpName;
                // returnData.primaryCarePhysicianAddress = `${paperworkData.pcpStreet}, ${paperworkData.pcpCity}, ${paperworkData.pcpState} ${paperworkData.pcpZip}`;
                returnData.primaryCarePhysicianStreet = paperworkData.pcpStreet;
                returnData.primaryCarePhysicianCity = paperworkData.pcpCity;
                returnData.primaryCarePhysicianState = paperworkData.pcpState;
                returnData.primaryCarePhysicianZipCode = paperworkData.pcpZip;
                returnData.primaryCarePhysicianPhone = paperworkData.pcpPhone;
                returnData.lastVisit = paperworkData.pcpLastVisited;

                // existing coverage
                if (paperworkData?.existingCoverage) {
                    returnData.existingCoverage = paperworkData?.existingCoverage;
                    returnData.existingCoverageAmount = paperworkData?.existingCoverageAmount;
                    returnData.existingCarrier = paperworkData?.coverageProviderName;
                    returnData.existingPolicyType = computed(() => getPolicyType(paperworkData?.existingPolicyType));
                }

                // past life insurance application within last 12 mnths.
                if (paperworkData?.pastApplication) {
                    returnData.shoppedCoverage = paperworkData?.pastApplication;
                    returnData.shoppedCoverageAmount = paperworkData?.pastApplicationCoverageAmount;
                    returnData.shoppedPolicyType = computed(() =>
                        getPolicyType(paperworkData?.pastApplicationPolicyType),
                    );
                }

                // Mother/Father Fields
                returnData.fatherLiving = paperworkData.fatherStillLiving;
                /* eslint-disable default-case */
                switch (paperworkData.fatherStillLiving.toLowerCase()) {
                    case 'yes':
                        returnData.fatherAge = paperworkData.fatherAge;
                        break;
                    case 'no':
                        returnData.fatherPassingAge = paperworkData.fatherPassedAtAge;
                        returnData.fatherPassingCause = paperworkData.fatherCauseOfPassing;
                        break;
                    case 'unknown':
                        returnData.fatherUnknown = paperworkData.fatherAdditionalDetails;
                        break;
                }
                returnData.motherLiving = paperworkData.motherStillLiving;
                returnData.visitReason = paperworkData.pcpVisitReason;
                returnData.visitOutcome = paperworkData.pcpVisitOutcome;
                returnData.hasPrimaryCarePhysician = paperworkData.showPCP === 'Yes';

                /* eslint-disable default-case */
                switch (paperworkData.motherStillLiving.toLowerCase()) {
                    case 'yes':
                        returnData.motherAge = paperworkData.motherAge;
                        break;
                    case 'no':
                        returnData.motherPassingAge = paperworkData.motherPassedAtAge;
                        returnData.motherPassingCause = paperworkData.motherCauseOfPassing;
                        break;
                    case 'unknown':
                        returnData.motherUnknown = paperworkData.motherAdditionalDetails;
                        break;
                }
                returnData.weightChanged = paperworkData.weightChanged === 'Yes';
            }
            const timeSortedPaperworks = customerPortalData?.paperworks.sort(
                (a: any, b: any) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt),
            );
            const confirmedPaperworkBirthStatus = timeSortedPaperworks.find(
                (r) =>
                    r.status === PaperworkStatusType.CONFIRMED &&
                    r.form_spec.label === PaperworkFormSpecType.BIRTH_STATUS,
            );
            if (confirmedPaperworkBirthStatus) {
                const paperworkData = JSON.parse(confirmedPaperworkBirthStatus.completed_paperwork);
                returnData.birthCountry = paperworkData.birthCountry;
                returnData.birthState =
                    paperworkData.birthCountry === 'united states' ? paperworkData.birthState : 'NotbornintheUS';
            }

            // Employment Info
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let employmentInfo: any = customerPortalData.paperworks.find(
                (r) =>
                    r.status === PaperworkStatusType.CONFIRMED &&
                    r.form_spec.label === PaperworkFormSpecType.EMPLOYMENT,
            );
            let employmentData;
            if (employmentInfo) {
                employmentData = JSON.parse(employmentInfo.completed_paperwork);
            } else {
                employmentInfo = customerPortalData.requirements.find((r) => r.type === 'employmentInfo');
                if (employmentInfo && employmentInfo.data_json) {
                    employmentData = JSON.parse(employmentInfo.data_json);
                }
            }
            if (employmentData) {
                returnData.nameOfEmployer = employmentData.employerName;
                returnData.industryDuties = removeFormattingCharacters(employmentData.industryDescription);
                returnData.occupation = employmentData.occupation;
                if (employmentData.employmentStartDate) {
                    const employmentDate = employmentData.employmentStartDate.split('/');
                    returnData.currentlyEmployed = 'Employed';
                    if (employmentDate && employmentDate.length > 1) {
                        const yearOfEmployed = employmentDate[1];
                        returnData.timeEmployed = new Date().getFullYear() - yearOfEmployed;

                        const d = new Date();
                        d.setDate(1);
                        d.setMonth(parseInt(employmentDate[0], 10) - 1);
                        d.setFullYear(parseInt(employmentDate[1], 10));

                        returnData.employmentDays = Math.round((Date.now() - d.getTime()) / 86400000);
                    }
                }
                returnData.currentSelfEmployed = employmentData.isSelfEmployed;
                returnData.prevSelfEmployed = employmentData.prevIsSelfEmployed;
                returnData.prevEmployName = employmentData.prevEmployerName;
                returnData.prevEmployStart = employmentData.prevEmploymentStartDate;
                returnData.prevEmployOccup = employmentData.prevOccupation;
                returnData.prevEmployIndustry = removeFormattingCharacters(employmentData.prevIndustryDescription);
            }

            const financialPaperwork = customerPortalData.paperworks.find(
                (paperwork) =>
                    paperwork.status === PaperworkStatusType.CONFIRMED &&
                    paperwork.form_spec.label === PaperworkFormSpecType.FINANCIAL_SUITABILITY,
            );
            if (financialPaperwork) {
                let financialPaperworkData;
                try {
                    financialPaperworkData = JSON.parse(financialPaperwork.completed_paperwork)?.financialSuitability;
                } catch (error) {
                    console.error('Failed to json parse financial paperwork data');
                }
                returnData.primaryFundingSource = Utils.MakeVarName(
                    financialPaperworkData?.primarySourceOfFunding || '',
                );
                returnData.equitableCDS = financialPaperworkData?.assetEstimate.cash;
                returnData.equitableStocks = financialPaperworkData?.assetEstimate.stocks;
                returnData.estimatedNetWorth = financialPaperworkData?.estimatedNetWorth;
                returnData.investedAssets = financialPaperworkData?.assetEstimate.total;
                returnData.equitableBonds = financialPaperworkData?.assetEstimate.bonds;
                returnData.equitableAnnuities = financialPaperworkData?.assetEstimate.annuities;
                returnData.investmentObjective = financialPaperworkData?.primaryPurposeOfInvestment?.replace(/\s/g, '');
                returnData.investmentTimeFrame = Utils.MakeVarName(financialPaperworkData?.whenHitObjective || '');

                returnData.equitableMutualFunds = financialPaperworkData?.assetEstimate.mutualFunds;
                returnData.equitableOtherProperties = financialPaperworkData?.assetEstimate.otherProperties;

                const InvestmentRiskValue = {
                    low: 'Low risk',
                    moderate: 'Moderate risk',
                    high: 'High risk',
                    none: '',
                };

                returnData.investmentRisk = Utils.MakeVarName(
                    InvestmentRiskValue[state?.phase1.investmentRisk || 'none'],
                );

                // returnData.investmentRisk = financialPaperworkData?.assetEstimate.investmentRisk;
                // returnData.annualIncomeFinancial = financialPaperworkData?.assetEstimate.total;
                returnData.equitableRetirementAccounts = financialPaperworkData?.assetEstimate.retirementAccounts;
                returnData.equitableOtherValue = financialPaperworkData?.assetEstimate.other;
                returnData.investmentGeneralKnowledge = financialPaperworkData?.investmentKnowledge;
                returnData.effectiveTaxRate = Utils.MakeVarName(financialPaperworkData?.effectiveTaxRate || '');
                returnData.equitableTotalAsset = financialPaperworkData?.assetEstimate.total;
                returnData.annualExpenses = Utils.MakeVarName(financialPaperworkData?.annualExpenses || '');
                returnData.emergencyFundAmount = Utils.MakeVarName(financialPaperworkData?.emergencySavings || '');
                returnData.equitableInvenstmentExpWithCDs = financialPaperworkData?.investmentExperience.cds;
                returnData.equitableInvenstmentExpWithStocks = financialPaperworkData?.investmentExperience.stocks;
                returnData.equitableInvenstmentExpWithBonds = financialPaperworkData?.investmentExperience.bonds;
                returnData.equitableInvenstmentExpWithAnnuities =
                    financialPaperworkData?.investmentExperience.annuities;
                returnData.equitableInvenstmentExpWithMutualFunds =
                    financialPaperworkData?.investmentExperience.mutualFunds;

                returnData.equitableInvestmentPurposeRecommendation =
                    financialPaperworkData?.primaryPurposeOfInvestment?.replace(/\s/g, '');

                returnData.equitableInvestmentRisk = Utils.MakeVarName(financialPaperworkData?.riskAddressed || '');
                returnData.equitableEffectiveTaxRate = Utils.MakeVarName(
                    financialPaperworkData?.effectiveTaxRate || '',
                );

                returnData.equitableConcerns = financialPaperworkData?.whatConcernsMost;
                returnData.totalAssets = financialPaperworkData?.assetEstimate.total;

                returnData.isPrincipalProtectionOrIncomeGuarantees = financialPaperworkData?.importanceOfProtection;
            }
        }
    }

    if (customerQuoteData?.length) {
        const quoteMap = new Map();
        customerQuoteData[0].v.properties.forEach((prop) => {
            quoteMap.set(prop.property, prop.value);
        });
        if (quoteMap.has('contribution')) {
            returnData.monthlyBudget = quoteMap.get('contribution');
        }
        if (quoteMap.get('user_selected_category') === productTypeMap.VUL) {
            returnData.firstCarrierQuoted = quoteMap.get('user_selected_vul_carrier')
                ? Utils.MakeVarName(quoteMap.get('user_selected_vul_carrier'))
                : 'Equitable';
            returnData.firstCoverageAmount = quoteMap.get('user_selected_vul_coverage') ?? 100000;
            returnData.firstMonthlyPremium = quoteMap.get('user_selected_vul_premium') ?? quoteMap.get('contribution');
            returnData.firstProductType = productTypeMap.VUL;
        } else {
            // determine if combo
            const isCombo =
                !!quoteMap.get('user_selected_perm_carrier') && !!quoteMap.get('user_selected_term_carrier');
            if (isCombo) {
                returnData.firstCarrierQuoted = Utils.MakeVarName(quoteMap.get('user_selected_perm_carrier'));
                returnData.firstCoverageAmount = quoteMap.get('user_selected_perm_coverage');
                returnData.firstMonthlyPremium = quoteMap.get('user_selected_perm_premium');
                returnData.firstProductType =
                    productTypeMap[quoteMap.get('user_selected_category')] ?? productTypeMap.IUL;
                returnData.secondCarrierQuoted = Utils.MakeVarName(quoteMap.get('user_selected_term_carrier'));
                returnData.secondCoverageAmount = quoteMap.get('user_selected_term_coverage');
                returnData.secondMonthlyPremium = quoteMap.get('user_selected_term_premium');
                returnData.secondProductType = productTypeMap[quoteMap.get('term_period')];
            } else if (quoteMap.get('user_selected_perm_carrier')) {
                returnData.firstCarrierQuoted = Utils.MakeVarName(quoteMap.get('user_selected_perm_carrier'));
                returnData.firstCoverageAmount = quoteMap.get('user_selected_perm_coverage');
                returnData.firstMonthlyPremium = quoteMap.get('user_selected_perm_premium');
                returnData.firstProductType =
                    productTypeMap[quoteMap.get('user_selected_category')] ?? productTypeMap.IUL;
            } else if (quoteMap.get('user_selected_term_carrier')) {
                returnData.firstCarrierQuoted = Utils.MakeVarName(quoteMap.get('user_selected_term_carrier'));
                returnData.firstCoverageAmount = quoteMap.get('user_selected_term_coverage');
                returnData.firstMonthlyPremium = quoteMap.get('user_selected_term_premium');
                returnData.firstProductType = productTypeMap[quoteMap.get('term_period')];
            }
        }
    }

    return returnData;
};

export default convertWalkthroughData;
